import { IconProps } from '@euler/app/components/icons/types';
import { MediaUploadStatusOverlay } from '@euler/app/components/media/UploadStatusOverlay';
import { Typography } from '@euler/app/components/typography';
import { useCurrentUserDangerously } from '@euler/app/flows/auth/context';
import { useMediaCaptureWithAnnotation } from '@euler/app/flows/media/functions/useMediaCaptureWithAnnotation';
import { useTakePhoto } from '@euler/app/flows/media/functions/useTakePhoto';
import { TaskImageWatermark } from '@euler/app/flows/task/components/ImageWatermark';
import { SiteIcon } from '@euler/app/flows/task/components/SiteIcon';
import {
  mediaUploadEventTypeToStatus,
  MediaUploadTask,
  MediaUploadTaskEvent,
} from '@euler/app/flows/task/functions/task-detail/MediaFileUploadController';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { ErrorText } from '@euler/components/form/Input';
import { Icon } from '@euler/components/Icomoon';
import { AdvancedImage } from '@euler/components/image/AdvancedImage';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { getValidateErrorMessage } from '@euler/lib/form/controller';
import { ImageAnnotationScene, SiteInspectionType } from '@euler/model/enum';
import { delayed, showAlert } from '@euler/utils';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { MotiView } from 'moti';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import {
  FlatList,
  ListRenderItem,
  StyleProp,
  StyleSheet,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { Path, Svg } from 'react-native-svg';
import { useOrderFlowContext } from '../../../../OrderFlowContext';
import { Cell } from '../components/Cell';
import { IconWrapper } from '../components/IconWrapper';
import { Row } from '../components/Row';

export const DashboardInfoRow = memo(
  ({ required, ...props }: { required?: boolean; disabled?: boolean }) => {
    const user = useCurrentUserDangerously();

    const {
      theme,
      navigation,
      orderDetail$,
      inventoryQuery,
      fileUploader,
      capabilities,
      storeConfiguration,
      formController: { form },
    } = useOrderFlowContext();

    const isWatermarkRequired =
      storeConfiguration.app?.orderFlow?.fields?.dashboardImage?.watermark ??
      false;

    const { rpx } = useResponsiveContext();

    const [uploadEvent, setUploadEvent] = useState<
      MediaUploadTaskEvent | undefined
    >();

    const [localFileUri, setLocalFileUri] = useState<string>();

    const { iconColor, placeholderColor, linkColor } = theme;

    const [orderDetail] = useBehaviorSubject(orderDetail$);

    const uploadTaskRef = useRef<MediaUploadTask<any>>();

    const troubleLightItems = useMemo(() => {
      return (
        orderDetail.preInspection
          ?.filter(x => x.inspectionType === SiteInspectionType.Dashboard)
          .map<TroubleLightListItem>(({ siteId }) => {
            const site = inventoryQuery.getSiteById(siteId);
            return {
              siteId,
              name: site?.name ?? '',
              siteCode: site?.code ?? '',
              icon: site?.iconUrl,
            };
          }) ?? []
      );
    }, [orderDetail, inventoryQuery]);

    const onPress = usePersistFn(() => {
      navigation.navigate('_dashboardInspection', {});
    });

    const upload = usePersistFn((fileUri: string) => {
      fileUploader.enqueue({
        type: 'image',
        contentType: 'image/jpeg',
        localFileUri: fileUri,
        parameters: { blurhash: true },
        context: { type: 'dashboard-image' },
        listener: async (event, task) => {
          uploadTaskRef.current = task;
          setUploadEvent(event);
          if (event.type === 'finished') {
            form.dashboardImgUrl.update(event.result.url);
            setUploadEvent(undefined);
            setLocalFileUri(undefined);
            uploadTaskRef.current = undefined;
          } else if (event.type === 'abort') {
            setLocalFileUri(undefined);
            setUploadEvent(undefined);
            uploadTaskRef.current = undefined;
          }
        },
      });
    });

    const takePhoto = useTakePhoto({ quality: 95 });
    const takePhotoWithAnnotation = useMediaCaptureWithAnnotation({
      scene: ImageAnnotationScene.Dashboard,
      photoOnly: true,
      multiple: false,
      maxMediaCountToSelect: 1,
    });

    const onTakePicture = usePersistFn(() => {
      if (isWatermarkRequired) {
        if (!form.licensePlateNo.value) {
          void showAlert('请先扫描或者录入车牌!');
          return;
        }
        takePhotoWithAnnotation({
          watermark: (scale?: number) => (
            <TaskImageWatermark
              licensePlateNo={form.licensePlateNo.value}
              title={'仪表拍照'}
              author={user.user.name}
              store={user.store.name}
              type="pre-inspection"
              scale={scale}
            />
          ),
          handleImageAnnotationResults: async results => {
            const result = results[0];
            if (result == null) return;
            setLocalFileUri(result.imageUri);
            upload(result.imageUri);
          },
        });
      } else {
        takePhoto(res => {
          setLocalFileUri(res.uri);
          upload(res.uri);
        });
      }
    });

    const onRetry = usePersistFn(() => {
      if (!localFileUri) return;
      upload(localFileUri);
    });

    const onDashboardImagePress = usePersistFn(() => {
      const imgUri = localFileUri ?? form.dashboardImgUrl.value;
      if (imgUri) {
        navigation.navigate('_mediaPreview', {
          type: 'photo',
          url: imgUri,
          retakeText: '重拍仪表照片',
          onRetake: capabilities?.preInspectionReadonly
            ? undefined
            : () => {
                navigation.goBack();
                delayed(() => {
                  if (uploadTaskRef.current) {
                    fileUploader.abort(uploadTaskRef.current.key);
                  }
                  onTakePicture();
                }, 500);
              },
        });
      } else {
        onTakePicture();
      }
    });

    return (
      <Row
        errorProp="dashboardImgUrl"
        icon={
          <IconWrapper>
            <RowIcon color={iconColor} />
          </IconWrapper>
        }
        detailIcon="disclosure"
        onPress={onPress}
        {...props}
      >
        <View
          css={`
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
          `}
        >
          <Cell
            required={required}
            label="仪表/油量信息"
            helpTextIcon={'error'}
            helpTextIconColor={StatusColors.Danger}
            helpText={
              uploadEvent?.type === 'error' ? (
                <HStack>
                  <Typography.Label color={StatusColors.Danger} size={rpx(12)}>
                    上传失败，
                  </Typography.Label>
                  <TouchableOpacity onPress={onRetry}>
                    <Typography.Label color={linkColor} size={rpx(12)}>
                      点击重新上传
                    </Typography.Label>
                  </TouchableOpacity>
                </HStack>
              ) : null
            }
            css={`
              flex: 1;
            `}
          >
            <TroubleLightIconList
              items={troubleLightItems}
              placeholderIconColor={placeholderColor}
            />
            {form.dashboardImgUrl.error ? (
              <ErrorText
                errorMsg={getValidateErrorMessage(form.dashboardImgUrl.error)}
                css={`
                  margin-top: ${rpx(8)}px;
                `}
              />
            ) : null}
          </Cell>
          <DashboardImageView
            dashboardImageUrl={form.dashboardImgUrl.value}
            localFileUri={localFileUri}
            uploadEvent={uploadEvent}
            onPress={onDashboardImagePress}
            onRetry={onRetry}
          />
        </View>
      </Row>
    );
  },
);

type TroubleLightListItem = {
  siteId: number;
  siteCode: string;
  name: string;
  icon?: string;
};

const troubleLightItemKeyExtractor = (x: TroubleLightListItem) => x.siteCode;

const TroubleLightIconList = memo(
  ({
    items,
    style,
    placeholderIconColor,
    placeholderTextStyle,
  }: {
    items: TroubleLightListItem[];
    style?: StyleProp<ViewStyle>;
    placeholderIconColor?: string;
    placeholderTextStyle?: StyleProp<TextStyle>;
  }) => {
    const { rpx } = useResponsiveContext();

    const renderItem: ListRenderItem<TroubleLightListItem> = useCallback(
      ({ item }) => {
        return (
          <DefectiveSiteIconCell
            item={item}
            css={`
              margin: 0 ${rpx(2)}px;
            `}
          />
        );
      },
      [rpx],
    );

    if (items.length === 0) {
      return (
        <HStack style={style} justifyContent="flex-start">
          <Typography.Label
            size={rpx(16)}
            color={placeholderIconColor ?? '#999'}
            light
            lineHeight={rpx(18)}
            css={`
              text-align: right;
              margin-left: 1px;
            `}
            style={placeholderTextStyle}
          >
            暂无仪表故障灯状态
          </Typography.Label>
        </HStack>
      );
    }
    return (
      <FlatList
        horizontal
        showsHorizontalScrollIndicator={false}
        data={items}
        keyExtractor={troubleLightItemKeyExtractor}
        renderItem={renderItem}
        style={style}
      />
    );
  },
);

const DefectiveSiteIconCell = memo(
  ({
    item,
    style,
  }: {
    item: TroubleLightListItem;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <MotiView
        from={{
          opacity: 0,
          translateX: rpx(55),
        }}
        animate={{ opacity: 1, translateX: 0 }}
        transition={{ delay: 100 }}
        style={style}
      >
        <SiteIcon
          url={item.icon}
          type={
            item.icon
              ? item.icon.includes('.svg')
                ? 'svg'
                : 'image'
              : undefined
          }
          size={rpx(16)}
          color="#888"
        />
      </MotiView>
    );
  },
);

const DashboardImageView = memo(
  ({
    dashboardImageUrl,
    localFileUri,
    uploadEvent,
    style,
    onPress,
    onRetry,
  }: {
    dashboardImageUrl?: string;
    localFileUri?: string;
    uploadEvent?: MediaUploadTaskEvent;
    style?: StyleProp<ViewStyle>;
    onPress?: () => void;
    onRetry?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const imgUrl = localFileUri ?? dashboardImageUrl;

    const status =
      uploadEvent != null
        ? mediaUploadEventTypeToStatus(uploadEvent)
        : undefined;

    const progress =
      uploadEvent?.type === 'progress' || uploadEvent?.type === 'compressing'
        ? uploadEvent.progress
        : uploadEvent?.type === 'begin'
        ? 0
        : undefined;

    return (
      <TouchableOpacity
        css={`
          width: ${rpx(67.5)}px;
          height: ${rpx(45)}px;
          border-width: ${imgUrl ? 0 : 1}px;
          border-color: rgba(208, 208, 208, 1);
          border-radius: ${imgUrl ? 0 : rpx(5)}px;
          overflow: hidden;
        `}
        style={style}
        onPress={onPress}
      >
        {imgUrl ? (
          <AdvancedImage
            uri={imgUrl}
            resizeMode="cover"
            css={`
              flex: 1;
              border-radius: ${rpx(5)}px;
              overflow: hidden;
            `}
          />
        ) : (
          <Center
            css={`
              flex: 1;
            `}
          >
            <Icon
              name="camera"
              size={rpx(20)}
              color={'rgba(208, 208, 208, 1)'}
            />
          </Center>
        )}
        {/* image upload status overlay */}
        {status != null && (
          <MediaUploadStatusOverlay
            status={status}
            progressViewSize={rpx(24)}
            progressStrokeWidth={rpx(2.5)}
            progressStrokeColor={'#ddd'}
            progress={progress}
            iconContainerSize={rpx(24)}
            errorIconSize={rpx(16)}
            waitIconSize={rpx(16)}
            style={StyleSheet.absoluteFill}
            onRetry={onRetry}
            css={`
              border-radius: ${rpx(5)}px;
            `}
          />
        )}
      </TouchableOpacity>
    );
  },
);

const RowIcon = memo(
  ({ size, style, color = '#0077FF', ...props }: IconProps) => {
    const { rpx } = useResponsiveContext();
    return (
      <Svg
        width={size ?? rpx(21)}
        height={size ?? rpx(18)}
        viewBox="0 0 21 18.0156"
        fill="none"
        style={style}
        {...props}
      >
        <Path
          d="M3.58899 17.4909C1.67871 15.6561 0.727539 13.3863 0.727539 10.6592C0.727539 5.25769 4.90576 0.727509 10.5355 0.727509C16.1199 0.727509 20.2725 5.30679 20.2725 10.6592C20.2725 13.0864 19.3674 15.3689 17.5461 17.5182L18.1011 17.9885C20.0299 15.7125 21 13.2657 21 10.6592C21 4.92014 16.5383 0 10.5355 0C4.49011 0 0 4.86847 0 10.6592C0 13.5829 1.03088 16.0424 3.08496 18.0156L3.58875 17.4909L3.58899 17.4909Z"
          fillRule="evenodd"
          fill={color}
        />
        <Path
          d="M12.5363 13.6818L13.2638 12.9543L15.0825 12.9543L15.8101 13.6818L12.5363 13.6818ZM7.44373 13.6818L8.17126 12.9543L9.98999 12.9543L10.7175 13.6818L7.44373 13.6818ZM12.5363 7.31613L15.8101 7.31613L15.0825 8.04364L13.2638 8.04364L12.5363 7.31613ZM7.44373 7.31613L10.7175 7.31613L9.98999 8.04364L8.17126 8.04364L7.44373 7.31613ZM4.89746 7.86176L5.62488 7.31613L5.62488 10.408L4.89746 9.8624L4.89746 7.86176ZM12.3544 10.5899L13.0819 11.1355L13.0819 12.7724L12.3544 13.4999L12.3544 10.5899ZM7.26184 10.5899L7.98938 11.1355L7.98938 12.7724L7.26184 13.4999L7.26184 10.5899ZM15.9919 10.5899L15.9919 13.4999L15.2644 12.7724L15.2644 11.1355L15.9919 10.5899ZM12.3544 7.49802L13.0819 8.22549L13.0819 9.8624L12.3544 10.408L12.3544 7.49802ZM15.9919 7.49802L15.9919 10.408L15.2644 9.8624L15.2644 8.22549L15.9919 7.49802ZM10.8993 7.49802L10.8993 10.408L10.1719 9.8624L10.1719 8.22549L10.8993 7.49802ZM4.89746 11.1355L5.62488 10.5899L5.62488 13.6818L4.89746 13.1362L4.89746 11.1355ZM8.07666 10.0443L10.0809 10.0443L10.6266 10.5081L10.0809 10.9536L8.07666 10.9536L7.53101 10.5088L8.07666 10.0443ZM7.60156 15.1019L7.87146 15.1019L7.87146 16.1815L8.44189 15.6392L8.79065 15.6392L8.20752 16.1815L8.84155 16.9556L8.49536 16.9556L8.02173 16.3546L7.87146 16.4947L7.87146 16.9556L7.60156 16.9556L7.60156 15.1019ZM9.62585 15.6035C9.81934 15.6035 9.95947 15.6773 10.0409 15.825C10.0995 15.7436 10.1631 15.685 10.2294 15.6519C10.2904 15.6188 10.3643 15.6035 10.4534 15.6035C10.5884 15.6035 10.7004 15.6468 10.7896 15.7385C10.8761 15.8276 10.9219 15.9447 10.9219 16.0924L10.9219 16.9556L10.652 16.9556L10.652 16.1382C10.652 16.0313 10.629 15.9524 10.5833 15.904C10.5375 15.8531 10.4611 15.8301 10.3591 15.8301C10.2828 15.8301 10.2217 15.8581 10.1708 15.9142C10.1199 15.9702 10.0944 16.0491 10.0944 16.1459L10.0944 16.9556L9.82446 16.9556L9.82446 16.1408C9.82446 15.932 9.73279 15.8301 9.55457 15.8301C9.46802 15.8301 9.39673 15.8632 9.3457 15.9294C9.28979 15.9931 9.26428 16.072 9.26428 16.1637L9.26428 16.9556L8.99438 16.9556L8.99438 15.6392L9.26428 15.6392L9.26428 15.7843C9.37122 15.6621 9.49341 15.6035 9.62585 15.6035ZM5.05115 15.9847L4.31165 16.7872C2.67688 15.2803 1.85791 13.2697 1.85791 10.7934C1.85791 8.80878 2.6073 6.65518 3.95056 5.01593C5.51685 3.10477 7.75964 2.00424 10.5 2.00424C13.0085 2.00424 14.8999 2.75958 16.6639 4.45483L15.9078 5.24164C14.3479 3.74261 12.7278 3.09549 10.5 3.09549C8.09241 3.09549 6.1571 4.04507 4.79468 5.70761C3.61316 7.14917 2.94922 9.05777 2.94922 10.7934C2.94922 12.9743 3.64868 14.692 5.05115 15.9847ZM16.3502 5.80002L17.2141 5.13324L17.9622 6.10248L17.0983 6.76923L16.3502 5.80002ZM17.4407 7.32776L18.4874 7.01932L18.8275 8.17239L17.7806 8.48105L17.4407 7.32794L17.4407 7.32776Z"
          fillRule="evenodd"
          fill={color}
        />
      </Svg>
    );
  },
);
